import { RSAA, type RSAAAction } from 'redux-api-middleware';
import {
  GetConfigWebApiHandler_getConfig,
  GetRenterStatusWebApiHandler_getRenterStatus,
} from '@zg-rentals/ts-rent-payment-client';
import { DatarouterError } from '@zg-rentals/web-base';
import { buildRenterPaymentsUrlV2, checkStatus, GET, POST } from '@common/paymentsUtils/api';
import {
  REQUEST_MARK_ACKNOWLEDGED,
  REQUEST_PAY_ONE_TIME_PAYMENT,
  REQUEST_RENT_DETAILS,
  REQUEST_RENTER_STATUS,
  REQUEST_UPDATE_MODE_LINK_TOKEN,
  REQUEST_USER_API,
} from '@renter-hub/constants/actionTypes';
import RenterStatusModel from '@renter-hub/models/RenterStatus';
import ConfigModel from '@renter-hub/models/Config';
import { MoveInCostsModel } from '@renter-hub/models/MoveInCosts';
import { formatTimestampToYMDString } from '@common/paymentsUtils/dateUtils';
import StringConstants from '@common/constants/StringConstants';
import type { RequestPayOneTimePaymentInterface } from '@renter-hub/models/RequestsTypes';
import {
  mapDashboardResponseToRenterStatusInput,
  mapGetConfigResponseToConfig,
} from '@renter-hub/utils/mapRenterStatusResponse';

const renterHubProxy = StringConstants.API.RENTER_HUB.PROXY;
const renterHubApi = StringConstants.API.RENTER_HUB.API;
const renterHubApiV1 = renterHubProxy + renterHubApi;
const requestHeader = {
  'Content-Type': 'application/json',
};

export const getUserRenter = ({ requireStrongAuthentication }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${renterHubApiV1}/get?pageWithSensitiveInformation=${requireStrongAuthentication}`,
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [{ type: REQUEST_USER_API.INIT }, { type: REQUEST_USER_API.SUCCESS }, { type: REQUEST_USER_API.FAIL }],
  },
});

type RequestRenterStatusOptions = {
  fetchConfig?: boolean;
};

export const requestRenterStatus =
  (encryptedInvite = '', { fetchConfig = false }: RequestRenterStatusOptions = {}) =>
  async (dispatch) => {
    dispatch({ type: REQUEST_RENTER_STATUS.INIT });

    try {
      const [renterStatusResponse, configResult] = await Promise.all([
        GetRenterStatusWebApiHandler_getRenterStatus({ encryptedInvite }),
        fetchConfig
          ? GetConfigWebApiHandler_getConfig({}).then(
              (res) => res,
              () => null,
            )
          : Promise.resolve(null),
      ]);

      const successAction = {
        type: REQUEST_RENTER_STATUS.SUCCESS,
        payload: {
          renterStatus: RenterStatusModel(
            mapDashboardResponseToRenterStatusInput(renterStatusResponse.dashboardResponseDto),
          ),
          ...(configResult ? { config: ConfigModel(mapGetConfigResponseToConfig(configResult)) } : {}),
          httpStatus: 200,
        },
      };

      dispatch(successAction);
      return successAction;
    } catch (error) {
      const httpStatus = DatarouterError.is(error) ? error.responseStatus : undefined;
      const failAction = {
        type: REQUEST_RENTER_STATUS.FAIL,
        error: true,
        payload: {
          httpStatus,
          error: true,
          originalResponse: error,
        },
      };

      dispatch(failAction);
      return failAction;
    }
  };

export const requestRentDetailsFromInvite = (encryptedInvite: string): RSAAAction => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getRentDetailsFromInvite'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ encryptedInvite }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_RENT_DETAILS.INIT,
      {
        type: REQUEST_RENT_DETAILS.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => {
              const { data } = json;
              const { firstDueDate, lastDueDate, moveInCosts } = data;

              return {
                ...data,
                firstDueDate: formatTimestampToYMDString(firstDueDate),
                lastDueDate: formatTimestampToYMDString(lastDueDate),
                moveInCosts: MoveInCostsModel(moveInCosts),
              };
            })
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_RENT_DETAILS.FAIL,
    ],
  },
});

export const requestPayOneTimePayment = (paymentInfo: RequestPayOneTimePaymentInterface): RSAAAction => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/payOneTimePayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(paymentInfo),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_PAY_ONE_TIME_PAYMENT.INIT,
      {
        type: REQUEST_PAY_ONE_TIME_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_PAY_ONE_TIME_PAYMENT.FAIL,
    ],
  },
});

export const requestMarkAcknowledged = (actionId): RSAAAction => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/markAcknowledged'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ actionId }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_MARK_ACKNOWLEDGED.INIT,
      {
        type: REQUEST_MARK_ACKNOWLEDGED.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_MARK_ACKNOWLEDGED.FAIL,
    ],
  },
});

export const requestUpdateModeLinkToken = (externalId?: string): RSAAAction => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createUpdateModeLinkToken'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      ...(externalId && { externalId }),
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.INIT,
        meta: { externalId },
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.SUCCESS,
        meta: { externalId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: ConfigModel(json.data),
          })),
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.FAIL,
        meta: { externalId },
      },
    ],
  },
});

/**
 * Execute >1 requests in parallel
 * Use this when feature permissions may have changed, as well as renter status.
 */
export const getRachelDashboard =
  ({
    encryptedInvite = '',
    fetchConfig = true,
  }: {
    encryptedInvite?: string;
    propertyId?: string;
    fetchConfig?: boolean;
  }) =>
  async (dispatch) =>
    // oxlint-disable-next-line unicorn/no-single-promise-in-promise-methods -- callers destructure the resolved array (e.g. `const [x] = await dispatch(...)`); unwrapping would break that shape
    Promise.all([dispatch(requestRenterStatus(encryptedInvite, { fetchConfig }))]);
